// =============================================================================
// ui/tabs/Settings.tsx — the first ported tab.
// -----------------------------------------------------------------------------
// ⚠ FOUR SUB-TABS, because that is what users actually see. The old renderer
// ships BOTH layouts and picks at runtime:
//
//     appFlags.settingsLayout || 'grouped'   ← grouped is the default
//
// so the flat "classic" layout is the fallback, not the live one. A first pass
// here ported the flat sections and Josh's reply — "the 3 you show aren't in the
// same place as mine" — was the tell. Porting the branch that reads first in the
// source rather than the branch that renders in production is a whole class of
// parity failure, and nothing about the flat markup announces that it is dead.
//
// Pane membership below is transcribed from the grouped markup, not guessed.
//
// ── ⚠ NO INVENTORY HERE. THE `NotPorted` CARDS ARE THE INVENTORY ──
//
// This header used to carry a DONE / NOT YET list, and it went stale the moment
// anything shipped — the same rot that hit the Journal tab's header, the
// FilterDrawer's, and the journal's own on-screen card. The rule instead:
//
//   **every pane renders a `NotPorted` card naming what it is still missing,
//   and whoever ships a control deletes its name from that card in the same
//   commit.** A test asserts the card is still there.
//
// So the screen states its own gaps and cannot disagree with a comment.
//
// ⚠ THREE SETTINGS WERE READ AND NOT WRITABLE, which is worse than missing: the
// effect is visible and the cause is not. Default Risk filled Risk and R:R on
// every trade with no stored plan and blanked both R columns when unset. The
// sidebar honoured `hiddenTabs` with nothing able to edit it. And the Dashboard
// drew Goals Progress off rows nothing here could create, edit or delete. All
// three now have editors.
//
// 🔴 **If you find a fourth, it outranks whatever else is on the list**, and the
// way to look is to ask of every card that renders a preference: what edits this?
//
// ── THE PATTERN THE OTHER SIXTEEN TABS COPY ──
//   1. Read through `usePreferences()`. Never load in a component.
//   2. Render nothing form-shaped until the read SUCCEEDED.
//   3. One-field patch per control. No Save button assembling an object —
//      that object is what reverts a field the screen never showed.
//   4. No arithmetic, no domain import.
// =============================================================================

import { useState, type ReactNode } from 'react';
import { usePreferences } from '../state/preferences';
import { useSupabase } from '../state/supabase';
import { DefaultRiskEditor } from './DefaultRiskEditor';
import { GoalsEditor } from './GoalsEditor';
import { MistakeTagEditor } from './MistakeTagEditor';
import { CustomTagEditor } from './CustomTagEditor';
import { ConfluenceEditor } from './ConfluenceEditor';
import { ContractLibrary } from './ContractLibrary';
import { DiscordWebhooks } from './DiscordWebhooks';
import { SidebarVisibility } from './SidebarVisibility';
import { SuppressedPositions } from './SuppressedPositions';
import { YourDataCard } from '../components/YourDataCard';
import { RestoreCard } from './RestoreCard';
import { ReimportCard } from './ReimportCard';
import { useAuthUser } from '../state/useAuthUser';
import type { DayBoundaryMode } from '../../io/sync/preferences';

/**
 * The trading-day clock, grouped as the old client groups it.
 *
 * 🔴 TEN OF BETA'S TWENTY WERE MISSING, and this is not a cosmetic shortfall:
 * this setting decides where every day boundary falls, so a trader in Phoenix,
 * Alaska, Hawaii, Frankfurt, Moscow, Dubai, India, Hong Kong, Shanghai or
 * Auckland could not set their own trading day at all. Every date-bucketed
 * number in the app — the calendar, weekday attribution, goal periods, journal
 * day-keying — would be filed against somebody else's clock.
 *
 * ⚠ GROUPED, because twenty flat options is a scroll. The groups and their
 * order are beta's (`SESSION_TIMEZONES`, app/index.html:5836).
 *
 * ⚠ The two US labels carry more than beta's do — "US futures default" and "CME
 * floor time". Those predate this change and are kept: they answer the question
 * a futures trader is actually asking of this list.
 */
const TIMEZONES: readonly { group: string; zones: readonly { value: string; label: string }[] }[] = [
  { group: 'US', zones: [
    { value: 'America/New_York', label: 'Eastern (New York) — US futures default' },
    { value: 'America/Chicago', label: 'Central (Chicago) — CME floor time' },
    { value: 'America/Denver', label: 'Mountain (Denver)' },
    { value: 'America/Phoenix', label: 'Arizona (no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
    { value: 'America/Anchorage', label: 'Alaska' },
    { value: 'Pacific/Honolulu', label: 'Hawaii' },
  ] },
  { group: 'Europe', zones: [
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Berlin', label: 'Frankfurt / Berlin' },
    { value: 'Europe/Zurich', label: 'Zurich' },
    { value: 'Europe/Moscow', label: 'Moscow' },
  ] },
  { group: 'Asia / Pacific', zones: [
    { value: 'Asia/Dubai', label: 'Dubai' },
    { value: 'Asia/Kolkata', label: 'India (Kolkata)' },
    { value: 'Asia/Singapore', label: 'Singapore' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
    { value: 'Asia/Shanghai', label: 'Shanghai' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Australia/Sydney', label: 'Sydney' },
    { value: 'Pacific/Auckland', label: 'Auckland' },
  ] },
  { group: 'Other', zones: [
    { value: 'UTC', label: 'UTC' },
  ] },
];

/** Every offered zone, flat — for the "is the stored one listed?" check. */
const TIMEZONE_VALUES: readonly string[] = TIMEZONES.flatMap((g) => g.zones.map((z) => z.value));

type Pane = 'app' | 'prefs' | 'trade' | 'backup';

/** Labels and order transcribed from the grouped layout's `.sg-strip`. */
const PANES: readonly { id: Pane; label: string }[] = [
  { id: 'app', label: 'App Settings' },
  { id: 'prefs', label: 'Preferences' },
  { id: 'trade', label: 'Trade Management' },
  { id: 'backup', label: 'Backup & Restore' },
];

export function SettingsTab() {
  const { phase, prefs, error, saveError, update, reload } = usePreferences();
  const [pane, setPane] = useState<Pane>('app');
  /**
   * The day boundary the trader just moved to, or null if they have not touched
   * it this visit — see the notice under that control.
   */
  const [boundaryMoved, setBoundaryMoved] = useState<DayBoundaryMode | null>(null);

  if (phase === 'loading') return <p className="muted">Loading your settings…</p>;

  // ⚠ The error state, NOT the form with defaults in it. This branch is the
  // entire point of the state layer.
  if (phase === 'failed' || !prefs) {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your settings</h3>
        <p className="muted">
          Nothing has been changed. Your settings are safe — this screen just
          can’t read them right now.
        </p>
        {error ? <p className="muted"><code>{error}</code></p> : null}
        <button type="button" className="btn primary" onClick={reload}>Try again</button>
      </div>
    );
  }

  return (
    <div className="settings-tab">
      <div className="sg-strip" role="tablist">
        {PANES.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={pane === p.id}
            className={`sg-tab${pane === p.id ? ' on' : ''}`}
            data-sg-tab={p.id}
            onClick={() => setPane(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {saveError ? (
        <div className="card" role="alert">
          <b>That change didn’t save.</b>{' '}
          <span className="muted">
            The value below is what you entered, not what’s stored. <code>{saveError}</code>
          </span>
        </div>
      ) : null}

      {pane === 'app' ? (
        <>
          <Section title="App Settings">
            <Field label="Display name" hint="Used in the dashboard greeting. Leave blank for “Good morning” without a name.">
              <TextSetting
                placeholder="Your first name"
                value={prefs.displayName}
                onCommit={(v) => void update({ displayName: v })}
              />
            </Field>
          </Section>

          {/* 🔴 SECOND, BEFORE the trading-day controls. Beta's App pane runs
              Display Name · Discord · Session Timezone · Day Boundary in one
              card (index.html:49639-49664); this sat LAST until 2026-09-17.
              Josh: keep beta's order, so a user switching clients finds things
              where they left them. */}
          <DiscordWebhooks />

          {/* ⚠ BETA HAS ONE CARD HERE AND THIS CLIENT KEEPS TWO, deliberately
              (Josh, 2026-09-17). The day boundary silently reframes every number
              in the app, and this heading and its blurb are what say so — the
              ONE place the order rule above is knowingly not followed to the
              letter. Owed to PARITY-DELTAS.md. */}
          <Section
            title="Trading day"
            blurb="Your trading-day clock — aligns chart times, session grouping and date-range filters."
          >
            <Field label="Session timezone" hint="Default Eastern (US futures).">
              <select
                value={prefs.sessionTimezone}
                onChange={(e) => void update({ sessionTimezone: e.target.value })}
              >
                {/* A stored value we don't offer must still be selectable, or
                    changing anything else would silently move the user's
                    timezone to whichever option happened to be first.
                    ⚠ This mattered more before the list grew: ten of the zones
                    below were absent, so anyone outside the offered ten was
                    relying on exactly this branch to keep their own setting. */}
                {TIMEZONE_VALUES.includes(prefs.sessionTimezone) ? null : (
                  <option value={prefs.sessionTimezone}>{prefs.sessionTimezone}</option>
                )}
                {TIMEZONES.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.zones.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field
              label="Day boundary"
              hint="Session mode reframes every aggregation: a “day” is the CME Globex session, so “today” started yesterday at 6PM ET. Affects calendar grouping, weekday attribution, date-range filters, goal periods and journal day-keying."
            >
              <select
                value={prefs.dayBoundaryMode}
                onChange={(e) => {
                  const next = e.target.value as DayBoundaryMode;
                  // ⚠ Only on an ACTUAL change — beta guards its announcement
                  // with `modeChanged`, and a notice that fires when nothing
                  // moved is one the reader learns to disbelieve.
                  if (next !== prefs.dayBoundaryMode) setBoundaryMoved(next);
                  void update({ dayBoundaryMode: next });
                }}
              >
                <option value="calendar">Calendar (midnight → midnight, local)</option>
                <option value="session">Session (CME futures: 6PM ET prev-day → 5PM ET)</option>
              </select>
            </Field>

            {/* 🚨 THIS SWITCH MOVES EVERY DATE-BUCKETED NUMBER IN THE APP, and
                nothing else on screen says so. The hint above explains what the
                two modes MEAN; this says the figures the reader has already
                looked at have just been re-filed.

                ⚠ NOT A TOAST. Beta fires one for six seconds (index.html:49875)
                — this client has no toast system at all, deliberately (D204:
                every ported surface renders its own inline status), so the same
                fact is a line that stays until the reader leaves the tab. Its
                words are beta's.

                ⚠ OUTSIDE the `Field`, which is a `<label>`: inside, this
                sentence would be appended to the select's own accessible name
                and read out on every focus. */}
            {boundaryMoved ? (
              <p className="field-notice" role="status">
                Day boundary mode → {boundaryMoved}. All aggregations will use the
                new boundary on the next view.
              </p>
            ) : null}
          </Section>

          <NotPorted items={['Theme']} />
        </>
      ) : null}

      {pane === 'prefs' ? (
        <>
          <MistakeTagEditor />
          <CustomTagEditor />
          <SidebarVisibility />
          {/* ⚠ AFTER Sidebar Visibility, which is the old client's order in this
              pane: mistake tags, controllable-loss signals, sidebar, Goals,
              Trade Form Confluences. */}
          <GoalsEditor />
          {/* 🔴 LAST, AFTER GOALS — the final card in the list the comment right
              above has spelled out since this pane was built. The confluence
              editor was nonetheless added THIRD and stayed there, because the
              only ordering test asserted which pane each control was on and
              never the sequence. Corrected 2026-09-17; now pinned by one. */}
          <ConfluenceEditor />
          <NotPorted items={[
            'Theme (light mode is not built here yet)',
            'the three layout pickers (this client ships one layout each)',
          ]} />
        </>
      ) : null}

      {pane === 'trade' ? (
        <>
          {/* ⚠ FIRST in this pane, which is the old client's order: Contract
              Library, Default Risk, Open-position warnings, Unfinished
              Positions. */}
          <ContractLibrary />

          <DefaultRiskEditor
            value={prefs.defaultRiskByInstrument}
            onChange={(next) => void update({ defaultRiskByInstrument: next })}
          />

          <Section
            title="Open-position warnings"
            blurb="How long after entry an unfilled position is still treated as “live”. Increase if you swing-hold; set 0 to always warn on dangling open positions."
          >
            <Field label="Live-trade window (hours)">
              <input
                type="number"
                min={0}
                max={48}
                step={0.5}
                value={prefs.openPositionLiveTradeHours}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  // ⚠ THE EMPTY STRING IS CHECKED BEFORE Number(), and that
                  // order is the guard. `Number('')` is **0**, not NaN — so a
                  // NaN-only check lets a cleared field persist 0, which for
                  // this setting means "always warn". Clearing the box to retype
                  // "12" would have written 0 on the way past.
                  //
                  // There is deliberately no isFinite check after this: a
                  // type=number input reports '' for anything non-numeric, so
                  // NaN is unreachable and no control could make such a guard
                  // fail. It comes back WITH a test if this becomes type=text.
                  if (raw === '') return;
                  void update({ openPositionLiveTradeHours: Number(raw) });
                }}
              />
            </Field>
          </Section>

          <SuppressedPositions />
        </>
      ) : null}

      {pane === 'backup' ? (
        <>
          {/* 🚨 BETA'S ORDER, WHICH IS RE-IMPORT · DATA · SESSION
              (index.html:49793-49799). Sign Out is the last card on the screen.

              `BackupExportCard` and `RestoreCard` are both halves of beta's one
              "Data" card — export in the first because it is SHARED with My
              Account, import and reset in the second because beta's My Account
              has neither. See `RestoreCard`'s header.

              🔴 AND THE `NotPorted` CARD IS GONE RATHER THAN EMPTIED. This pane
              named Re-import as its last gap; with it routed here the pane has
              none, and "Not ported yet: nothing" is a sentence with no reader.
              Same precedent as My Account and the Contract Library, each of
              which deleted its card in the commit that closed its last item. */}
          <ReimportCard />
          <BackupExportCard />
          <RestoreCard />
          <SignOutCard />
        </>
      ) : null}
    </div>
  );
}

/**
 * The export, on the pane beta keeps it on.
 *
 * ── 🔴 THE CAPABILITY WAS NEVER MISSING. ONLY THE ROUTE TO IT WAS ──
 *
 * TCR-40 was filed and worked as "there is no export in this client", on the
 * strength of one `grep` whose four search terms all happened to miss
 * `io/sync/exportAccount.ts`. A second export was built, merged and deployed
 * before anyone read the file that was already there. This card is the
 * correction: the same component My Account renders, on the pane beta puts it
 * on. ▶ The real gap was DISCOVERABILITY — a user looking for a backup goes to
 * Backup & Restore, not to My Account.
 *
 * ⚠ IT RESOLVES THE USER RATHER THAN TAKING ONE FROM THE PANE, because Settings
 * has no user in hand and `useAuthUser` asks the auth service fresh. While that
 * read is in flight there is no button — an export control that cannot name who
 * it would export is one that fails after the click instead of before it.
 */
function BackupExportCard() {
  const { phase, user, error } = useAuthUser();

  if (phase === 'loading') {
    return (
      <section className="card">
        <h3>Your data</h3>
        <p className="muted">Checking who is signed in…</p>
      </section>
    );
  }

  // ⚠ Says the export is unavailable and why. The alternative — rendering the
  // button anyway — offers a backup this screen already knows it cannot build.
  if (!user) {
    return (
      <section className="card">
        <h3>Your data</h3>
        <p className="red" role="alert">
          The download is unavailable because your account could not be read.{' '}
          {error ?? 'The account service gave no reason.'}
        </p>
      </section>
    );
  }

  return <YourDataCard user={user} />;
}

/**
 * Sign out.
 *
 * ── ⚠ THERE WAS NO WAY TO LOG OUT OF THIS CLIENT AT ALL ──
 *
 * Not a parity gap so much as a missing exit. On a shared or borrowed machine
 * the only way out was to clear site data by hand.
 *
 * ⚠ IT ASKS FIRST, and the confirmation says the thing a trader actually wants
 * to know: the data is in the cloud and signing out does not touch it. Beta's
 * blurb says the same. Without it "Sign Out" beside "Reset Everything" reads
 * like it might be destructive.
 *
 * ⚠ NO NAVIGATION AFTERWARDS. `App` subscribes to `onAuthStateChange` and swaps
 * itself for the sign-in screen when the session ends — routing here as well
 * would be a second, racing source of truth for the same fact.
 */
function SignOutCard() {
  const { sb } = useSupabase();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signOut = async () => {
    setBusy(true);
    setError(null);
    const { error: e } = await sb.auth.signOut();
    // ⚠ On failure the button comes BACK rather than leaving a dead screen: a
    // failed sign-out that looks like a success is the one outcome that matters
    // here, because the user walks away from the machine believing it worked.
    if (e) { setBusy(false); setConfirming(false); setError(e.message); return; }
  };

  return (
    <section className="card">
      <h3>Session</h3>
      <p className="muted">
        Sign out of TRADECOACH. Your data stays in the cloud — sign back in with
        the same email and everything is where you left it.
      </p>
      {error ? (
        <p className="red" role="alert">
          <b>Still signed in.</b> {error}
        </p>
      ) : null}
      <div className="row gap-8">
        {confirming ? (
          <>
            <span className="muted">Sign out on this device?</span>
            <button type="button" className="btn" disabled={busy}
              onClick={() => setConfirming(false)}>Cancel</button>
            <button type="button" className="btn primary" disabled={busy}
              onClick={() => void signOut()}>
              {busy ? 'Signing out…' : 'Yes, sign out'}
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={() => setConfirming(true)}>
            Sign out
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * ⚠ Names what is missing rather than showing an empty pane. During a phased
 * port an empty screen is indistinguishable from a broken one, and the person
 * checking parity is exactly the one who needs to tell them apart.
 */
function NotPorted({ items }: { items: readonly string[] }) {
  return (
    <div className="card">
      <h3>Not ported yet</h3>
      <p className="muted">
        Still in the current client: {items.join(' · ')}. Phase 4 in progress —
        these depend on data the rebuild has not relocated yet.
      </p>
    </div>
  );
}

/**
 * A text setting that commits on BLUR, not per keystroke.
 *
 * ⚠ The selects above save on change because a select is one decision. A text
 * field is not: `onChange` there is one network write per character typed, and
 * each one races the last — the final stored value is whichever request happens
 * to land last, which on a slow connection is not necessarily the last thing
 * typed.
 *
 * Local draft state also means the input never fights the optimistic update
 * coming back through `prefs` mid-word.
 */
function TextSetting(
  { value, placeholder, onCommit }:
  { value: string; placeholder?: string; onCommit: (v: string) => void },
) {
  const [draft, setDraft] = useState(value);
  // Re-sync when the stored value changes underneath (a reload, another tab).
  const [seen, setSeen] = useState(value);
  if (value !== seen) { setSeen(value); setDraft(value); }

  return (
    <input
      type="text"
      placeholder={placeholder}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft !== value) onCommit(draft); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
}

function Section({ title, blurb, children }: { title: string; blurb?: string; children: ReactNode }) {
  return (
    <section className="card">
      <h3>{title}</h3>
      {blurb ? <p className="muted">{blurb}</p> : null}
      <div className="form-grid">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="muted field-hint">{hint}</span> : null}
    </label>
  );
}
